import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import styles from './ChapterContentEdit.module.css';

const EDGE_TTS_PREFIX = '/edge-tts';

function parseSentencesFromJson(raw) {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const data = JSON.parse(raw);
    const list = data?.sentences ?? data?.paragraphs ?? (Array.isArray(data) ? data : []);
    return list.filter((item) => item && typeof item === 'object');
  } catch {
    return [];
  }
}

function getSentenceId(item) {
  const id = item.sentence_id ?? item.id;
  if (id != null) return Number(id);
  return null;
}

function getZh(item) {
  return (
    item.content_zh ??
    item.sentence_content ??
    item.zh ??
    item.text ??
    item.text_cn ??
    ''
  );
}

function getEn(item) {
  return (
    item.content_en ??
    item.sentence_translation ??
    item.sentence_content ??
    item.en ??
    item.translation ??
    item.text_en ??
    ''
  );
}

function mergeContentToRows(content, contentTrans) {
  const contentList = parseSentencesFromJson(content);
  const transList = parseSentencesFromJson(contentTrans);
  const transById = {};
  transList.forEach((item) => {
    const sid = getSentenceId(item);
    if (sid != null) transById[sid] = getEn(item);
  });
  const rows = [];
  contentList.forEach((item) => {
    const sid = getSentenceId(item);
    if (sid == null) return;
    rows.push({
      sentence_id: sid,
      zh: getZh(item),
      en: transById[sid] ?? '',
    });
  });
  return rows;
}

function buildContentJson(originalContent, rows) {
  if (!originalContent || typeof originalContent !== 'string') {
    return JSON.stringify({
      sentences: rows.map((r) => ({ sentence_id: r.sentence_id, sentence_content: r.zh })),
    });
  }
  try {
    const data = JSON.parse(originalContent);
    const payload = { ...data };
    payload.sentences = rows.map((r) => ({
      sentence_id: r.sentence_id,
      sentence_content: r.zh,
    }));
    if (payload.sentence_count !== undefined) payload.sentence_count = rows.length;
    return JSON.stringify(payload, null, 2);
  } catch {
    return JSON.stringify({
      sentences: rows.map((r) => ({ sentence_id: r.sentence_id, sentence_content: r.zh })),
    });
  }
}

function buildTransJson(originalTrans, rows) {
  if (!originalTrans || typeof originalTrans !== 'string') {
    return JSON.stringify({
      sentences: rows.map((r) => ({ sentence_id: r.sentence_id, sentence_content: r.en })),
    });
  }
  try {
    const data = JSON.parse(originalTrans);
    const payload = { ...data };
    payload.sentences = rows.map((r) => ({
      sentence_id: r.sentence_id,
      sentence_content: r.en,
    }));
    if (payload.sentence_count !== undefined) payload.sentence_count = rows.length;
    return JSON.stringify(payload, null, 2);
  } catch {
    return JSON.stringify({
      sentences: rows.map((r) => ({ sentence_id: r.sentence_id, sentence_content: r.en })),
    });
  }
}

function ChapterContentEdit() {
  const [novels, setNovels] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [selectedNovelId, setSelectedNovelId] = useState('');
  const [selectedVolumeId, setSelectedVolumeId] = useState('');
  const [selectedChapterId, setSelectedChapterId] = useState('');
  const [loadingNovels, setLoadingNovels] = useState(true);
  const [loadingVolumes, setLoadingVolumes] = useState(false);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chapterTitle, setChapterTitle] = useState('');
  const [rawContent, setRawContent] = useState('');
  const [rawContentTrans, setRawContentTrans] = useState('');
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState('');
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voiceCn, setVoiceCn] = useState('');
  const [voiceEn, setVoiceEn] = useState('');
  const [selectedSentenceIds, setSelectedSentenceIds] = useState(new Set());
  const [regenTaskId, setRegenTaskId] = useState('');
  const [regenStatus, setRegenStatus] = useState('');
  const regenPollRef = useRef(null);

  const fetchNovels = useCallback(async () => {
    setLoadingNovels(true);
    try {
      const { data } = await api.get('/novels/', { params: { limit: 500 } });
      setNovels(Array.isArray(data) ? data : []);
    } catch (e) {
      setMessage('获取小说列表失败');
      setNovels([]);
    } finally {
      setLoadingNovels(false);
    }
  }, []);

  useEffect(() => {
    fetchNovels();
  }, [fetchNovels]);

  useEffect(() => {
    return () => {
      if (regenPollRef.current) clearInterval(regenPollRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setVoicesLoading(true);
    api.get(`${EDGE_TTS_PREFIX}/voices/all`).then((res) => {
      if (cancelled) return;
      const list = Array.isArray(res.data?.voices) ? res.data.voices : [];
      setVoices(list);
      if (list.length > 0 && !voiceCn) {
        const zh = list.find((v) => v.category === 'zh');
        if (zh) setVoiceCn(zh.shortName);
      }
      if (list.length > 0 && !voiceEn) {
        const en = list.find((v) => v.category === 'en');
        if (en) setVoiceEn(en.shortName);
      }
    }).catch(() => {
      if (!cancelled) setVoices([]);
    }).finally(() => {
      if (!cancelled) setVoicesLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const zhVoices = voices.filter((v) => v.category === 'zh');
  const enVoices = voices.filter((v) => v.category === 'en');

  useEffect(() => {
    if (!selectedNovelId) {
      setVolumes([]);
      setSelectedVolumeId('');
      setChapters([]);
      setSelectedChapterId('');
      return;
    }
    setLoadingVolumes(true);
    api
      .get('/volumes/', { params: { novel_id: Number(selectedNovelId), limit: 500 } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setVolumes(list);
        setSelectedVolumeId('');
        setChapters([]);
        setSelectedChapterId('');
      })
      .catch(() => {
        setMessage('获取卷列表失败');
        setVolumes([]);
      })
      .finally(() => setLoadingVolumes(false));
  }, [selectedNovelId]);

  useEffect(() => {
    if (!selectedVolumeId) {
      setChapters([]);
      setSelectedChapterId('');
      return;
    }
    setLoadingChapters(true);
    api
      .get('/chapters/', {
        params: { volume_id: Number(selectedVolumeId), limit: 500, lite: true },
      })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setChapters(list.sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)));
        setSelectedChapterId('');
      })
      .catch(() => {
        setMessage('获取章节列表失败');
        setChapters([]);
      })
      .finally(() => setLoadingChapters(false));
  }, [selectedVolumeId]);

  useEffect(() => {
    if (!selectedChapterId) {
      setRows([]);
      setChapterTitle('');
      setRawContent('');
      setRawContentTrans('');
      return;
    }
    setLoadingContent(true);
    setMessage('');
    api
      .get(`/chapters/${selectedChapterId}/content`)
      .then((res) => {
        const content = res.data?.content ?? '';
        const contentTrans = res.data?.content_trans ?? '';
        setChapterTitle(res.data?.title ?? '');
        setRawContent(content);
        setRawContentTrans(contentTrans);
        setRows(mergeContentToRows(content, contentTrans));
        setSelectedSentenceIds(new Set());
      })
      .catch(() => {
        setMessage('获取章节内容失败');
        setRows([]);
      })
      .finally(() => setLoadingContent(false));
  }, [selectedChapterId]);

  const toggleSentenceForRegen = useCallback((sentenceId) => {
    setSelectedSentenceIds((prev) => {
      const next = new Set(prev);
      if (next.has(sentenceId)) next.delete(sentenceId);
      else next.add(sentenceId);
      return next;
    });
  }, []);

  const selectAllForRegen = useCallback(() => {
    if (selectedSentenceIds.size === rows.length) {
      setSelectedSentenceIds(new Set());
    } else {
      setSelectedSentenceIds(new Set(rows.map((r) => r.sentence_id)));
    }
  }, [rows, selectedSentenceIds.size]);

  const handleRegenVoice = useCallback(async () => {
    if (!selectedChapterId || selectedSentenceIds.size === 0) {
      setMessage('请先选择要重新生成语音的句子');
      return;
    }
    if (!voiceCn || !voiceEn) {
      setMessage('请选择中文和英文说话人');
      return;
    }
    setMessage('');
    setRegenStatus('pending');
    try {
      const { data } = await api.post(`${EDGE_TTS_PREFIX}/tts/regenerate-sentences`, {
        chapter_id: Number(selectedChapterId),
        sentence_ids: [...selectedSentenceIds],
        voice_cn: voiceCn,
        voice_en: voiceEn,
      });
      const taskId = data?.task_id;
      if (!taskId) {
        setMessage('未返回 task_id');
        setRegenStatus('');
        return;
      }
      setRegenTaskId(taskId);
      setRegenStatus('processing');

      const poll = async () => {
        try {
          const res = await api.get(`${EDGE_TTS_PREFIX}/tts/regenerate-sentences/${taskId}`);
          const status = res.data?.status;
          if (status === 'completed') {
            setRegenStatus('completed');
            setMessage('语音重新生成完成，已更新 OSS 的 en.mp3 / cn.mp3 与 srt');
            if (regenPollRef.current) clearInterval(regenPollRef.current);
            return;
          }
          if (status === 'failed') {
            setRegenStatus('failed');
            setMessage(res.data?.error ?? '语音重新生成失败');
            if (regenPollRef.current) clearInterval(regenPollRef.current);
            return;
          }
        } catch {
          if (regenPollRef.current) clearInterval(regenPollRef.current);
          setRegenStatus('failed');
          setMessage('轮询任务状态失败');
        }
      };
      regenPollRef.current = setInterval(poll, 2000);
      poll();
    } catch (e) {
      setMessage(e?.response?.data?.detail ?? '发起语音重新生成失败');
      setRegenStatus('');
    }
  }, [selectedChapterId, selectedSentenceIds, voiceCn, voiceEn]);

  const handleZhChange = useCallback((index, value) => {
    setRows((prev) => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], zh: value };
      return next;
    });
  }, []);

  const handleEnChange = useCallback((index, value) => {
    setRows((prev) => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], en: value };
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedChapterId || !rows.length) {
      setMessage('请先选择章节并确保有句子数据');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const content = buildContentJson(rawContent, rows);
      const content_trans = buildTransJson(rawContentTrans, rows);
      await api.put(`/chapters/${selectedChapterId}/content`, { content, content_trans });
      setRawContent(content);
      setRawContentTrans(content_trans);
      setMessage('保存成功');
    } catch (e) {
      setMessage(e?.response?.data?.detail ?? '保存失败');
    } finally {
      setSaving(false);
    }
  }, [selectedChapterId, rows, rawContent, rawContentTrans]);

  const selectedNovel = novels.find((n) => String(n.id) === String(selectedNovelId));
  const selectedVolume = volumes.find((v) => String(v.id) === String(selectedVolumeId));
  const selectedChapter = chapters.find((c) => String(c.id) === String(selectedChapterId));

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>中英文修改</h1>
      <p className={styles.subtitle}>选择小说 → 卷 → 章节，编辑每句中文或英文后保存，将更新数据库中的 content / content_trans。</p>

      {message && (
        <div className={styles.message} role="alert">
          {message}
        </div>
      )}

      <div className={styles.selectors}>
        <div className={styles.selectGroup}>
          <label className={styles.label}>小说</label>
          <select
            className={styles.select}
            value={selectedNovelId}
            onChange={(e) => setSelectedNovelId(e.target.value)}
            disabled={loadingNovels}
          >
            <option value="">请选择小说</option>
            {novels.map((n) => (
              <option key={n.id} value={n.id}>
                {n.title}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.selectGroup}>
          <label className={styles.label}>卷</label>
          <select
            className={styles.select}
            value={selectedVolumeId}
            onChange={(e) => setSelectedVolumeId(e.target.value)}
            disabled={!selectedNovelId || loadingVolumes}
          >
            <option value="">请选择卷</option>
            {volumes.map((v) => (
              <option key={v.id} value={v.id}>
                {v.title}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.selectGroup}>
          <label className={styles.label}>章节</label>
          <select
            className={styles.select}
            value={selectedChapterId}
            onChange={(e) => setSelectedChapterId(e.target.value)}
            disabled={!selectedVolumeId || loadingChapters}
          >
            <option value="">请选择章节</option>
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedChapterId && (
        <>
          {loadingContent ? (
            <div className={styles.loading}>加载章节内容中…</div>
          ) : (
            <>
              <div className={styles.chapterHeader}>
                <span className={styles.chapterTitle}>
                  {selectedNovel?.title} — {selectedVolume?.title} — {chapterTitle || selectedChapter?.title}
                </span>
                <button
                  type="button"
                  className={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving || rows.length === 0}
                >
                  {saving ? '保存中…' : '保存'}
                </button>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th className={styles.thId}>#</th>
                      <th className={styles.thRegen}>
                        <span title="勾选后可在下方重新生成这些句子的语音">重生成</span>
                        <button type="button" className={styles.selectAllRegen} onClick={selectAllForRegen}>
                          {selectedSentenceIds.size === rows.length ? '取消全选' : '全选'}
                        </button>
                      </th>
                      <th className={styles.thZh}>中文</th>
                      <th className={styles.thEn}>英文</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={row.sentence_id}>
                        <td className={styles.tdId}>{row.sentence_id}</td>
                        <td className={styles.tdRegen}>
                          <input
                            type="checkbox"
                            checked={selectedSentenceIds.has(row.sentence_id)}
                            onChange={() => toggleSentenceForRegen(row.sentence_id)}
                            title="勾选以重新生成该句语音"
                          />
                        </td>
                        <td className={styles.tdZh}>
                          <textarea
                            className={styles.textarea}
                            value={row.zh}
                            onChange={(e) => handleZhChange(index, e.target.value)}
                            rows={2}
                          />
                        </td>
                        <td className={styles.tdEn}>
                          <textarea
                            className={styles.textarea}
                            value={row.en}
                            onChange={(e) => handleEnChange(index, e.target.value)}
                            rows={2}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <section className={styles.regenSection}>
                <h3 className={styles.regenTitle}>语音重新生成</h3>
                <p className={styles.regenHint}>
                  勾选上方需要更改语音的句子，选择中文/英文说话人后点击按钮。将使用 Edge TTS 生成选中句子的中英文语音，替换 OSS 上该章节的 en.mp3、cn.mp3 及对应 srt 后重新上传。
                </p>
                <div className={styles.regenRow}>
                  <div className={styles.selectGroup}>
                    <label className={styles.label}>中文说话人</label>
                    <select
                      className={styles.select}
                      value={voiceCn}
                      onChange={(e) => setVoiceCn(e.target.value)}
                      disabled={voicesLoading}
                    >
                      <option value="">请选择</option>
                      {zhVoices.map((v) => (
                        <option key={v.shortName} value={v.shortName}>
                          {v.shortName} {v.localName ? `(${v.localName})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.selectGroup}>
                    <label className={styles.label}>英文说话人</label>
                    <select
                      className={styles.select}
                      value={voiceEn}
                      onChange={(e) => setVoiceEn(e.target.value)}
                      disabled={voicesLoading}
                    >
                      <option value="">请选择</option>
                      {enVoices.map((v) => (
                        <option key={v.shortName} value={v.shortName}>
                          {v.shortName} {v.localName ? `(${v.localName})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className={styles.regenBtn}
                    onClick={handleRegenVoice}
                    disabled={voicesLoading || selectedSentenceIds.size === 0 || !voiceCn || !voiceEn || regenStatus === 'processing'}
                  >
                    {regenStatus === 'processing' ? '生成中…' : regenStatus === 'completed' ? '已完成' : '重新生成选中句子语音'}
                  </button>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default ChapterContentEdit;
